import { ChevronsUpDown } from 'lucide-react';
import { Fragment, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { default as Logo } from '@/assets/logos/miliboo-pom-min.svg?react';
import { ExternalLink } from '@/components/links/external-link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

export function ProjectSwitcher({
  projects,
}: {
  projects?: {
    name: string;
    logo: ReactElement;
    link: string | undefined;
    plan: string;
  }[];
}) {
  const { isMobile } = useSidebar();
  const { t } = useTranslation('navigation');

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              className='data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground'
              size='lg'
            >
              <div className='flex items-center'>
                <Logo className='size-8 text-sidebar-primary-foreground' />
              </div>
              <div className='ml-2 grid flex-1 text-left text-sm leading-tight'>
                <span className='truncate font-semibold'>Purchase Order</span>
                <span className='truncate text-xs'>Miliboo</span>
              </div>
              <ChevronsUpDown className='ml-auto size-4' />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align='start'
            className='min-w-52 rounded-lg'
            side={isMobile ? 'bottom' : 'right'}
            sideOffset={4}
          >
            {projects && projects.length > 0 ? (
              <>
                <DropdownMenuLabel className='text-secondary-foreground text-xs'>
                  {t('Projects')}
                </DropdownMenuLabel>
                {projects.map((project, index) => (
                  <>
                    {project.link && (
                      <Fragment key={project.name}>
                        <DropdownMenuItem asChild className='p-0'>
                          <ExternalLink
                            className='flex w-full cursor-pointer items-center gap-2 px-2 py-1.5'
                            href={project.link}
                          >
                            <div className='shrink-0'>{project.logo}</div>
                            <div className='mt-1 flex min-w-0 flex-1 flex-col'>
                              <span className='font-medium'>
                                {project.name}
                              </span>
                              <span className='text-muted-foreground text-xs'>
                                {t(project.plan)}
                              </span>
                            </div>
                          </ExternalLink>
                        </DropdownMenuItem>
                        {index !== projects.length - 1 && (
                          <DropdownMenuSeparator />
                        )}
                      </Fragment>
                    )}
                  </>
                ))}
              </>
            ) : (
              <DropdownMenuItem disabled>
                {t('No projects available')}
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
